import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { RefObject, useState } from 'react';
import {
	View, TouchableOpacity, StyleSheet, Platform,
	TextInput
} from 'react-native';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { CustomSearchBar } from '../../customSearchBar';
import { isValidAmount } from '../../../../redux/utils/discountUtils';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import CommonService from '../../../../shared/services/commonService';
import { paymentSaveType } from '../../../../redux/types/sellScreenTypes';
import { toggleSplitPayment } from '../../../../redux/slices/newSellscreenPaymentSlice';

const OnAccountPayment = ({ onClose, handleSave, initialValue = 0, notes = "", inputRef }: { onClose: () => void, handleSave: (value: paymentSaveType) => void, initialValue?: number, notes?: string, inputRef?: RefObject<TextInput | null> }) => {
	const dispatch = useAppDispatch()
	const [value, setValue] = useState<null | string>(initialValue ? initialValue?.toString() : null);
	const [reason, setReason] = useState<null | string>(notes ? notes : null);
	const { totalAmount, isSplitPayment, remainingAmount } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const { customerDetails } = useAppSelector((state) => state?.NewSellscreenSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

	const onAccountAmount = customerDetails?.onAccountDetails?.balance || 0
	const newTotalAmount = isSplitPayment ? (remainingAmount + initialValue) : totalAmount

	const onchangeText = (text: string | null) => {
		if (!text || isValidAmount(text)) {
			if ((Number(text) <= newTotalAmount) && (Number(text) <= onAccountAmount)) {
				setValue(text)
			}
		}
	}

	const disabledSave = () => {
		if (!onAccountAmount) return true
		if (isSplitPayment) {
			if (!value) {
				return true
			}
			return false
		} else {
			if (onAccountAmount < newTotalAmount) return true
			return false
		}
	}

	const fullPayment = () => {
		handleSave({
			amount: newTotalAmount,
			paidAmount: newTotalAmount,
			reason: reason || ""
		})
	}

	const partialPayment = (isFull?: boolean) => {
		const amount = isFull ? onAccountAmount : Number(value)
		handleSave({
			amount,
			paidAmount: amount,
			reason: reason || ""
		})
	}

	const handleSaveButton = () => {
		if (isSplitPayment) {
			partialPayment()
		} else {
			if (onAccountAmount >= newTotalAmount) {
				fullPayment()
			} else {
				dispatch(toggleSplitPayment())
				partialPayment(true)
			}
		}
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View>
						{isSplitPayment ? null : <View style={styles.header}>
							<ZenText content={'On Account Payment'} style={styles.headerTitle} />
							<TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
								<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
							</TouchableOpacity>
						</View>}

						<View style={styles.content}>
							<View style={{ flexDirection: "row", justifyContent: "space-evenly", alignItems: "center" }}>
								<View style={styles.headerCard}>
									<ZenText content={'On Account Balance'} style={[styles.label, { marginBottom: 0, color: theme?.grey, fontFamily: theme?.bold }]} />
									<ZenText content={CommonService?.currencyStandard(currencySymbol, onAccountAmount?.toString())} style={styles.totalAmount} />
								</View>
								{isSplitPayment ?
									<View style={styles.headerCard}>
										<ZenText content={'Balance'} style={[styles.label, { marginBottom: 0, color: theme?.grey, fontFamily: theme?.bold }]} />
										<ZenText content={CommonService?.currencyStandard(currencySymbol, (remainingAmount - (Number(value) - initialValue)) > 0 ? (remainingAmount - (Number(value) - initialValue))?.toString() : "0")} style={styles.totalAmount} />
									</View>
									: <View style={styles.headerCard}>
										<ZenText content={'Total Amount'} style={[styles.label, { marginBottom: 0, color: theme?.grey, fontFamily: theme?.bold }]} />
										<ZenText content={CommonService?.currencyStandard(currencySymbol, newTotalAmount?.toString())} style={styles.totalAmount} />
									</View>}
							</View>

							{/* Value Input */}
							{(isSplitPayment && onAccountAmount) ? <>
								<ZenText content='Payment Amount' style={styles.label} />

								<View style={{ paddingBottom: 24 }}>
									<CustomSearchBar
										inputRef={inputRef}
										placeholder='Enter a amount'
										search={value}
										setSearch={onchangeText}
										toggleOffset={() => { }}
										backgroundColor={theme?.iceWhite}
										leftIcon={false}
										border={true}
										height={50}
										keyboardType='number-pad'
										leftComponent={<ZenText content={currencySymbol} style={styles.currencyPrefix} />}
									/>
								</View>
							</> : <></>}

							<ZenText content='Payment Notes (Optional)' style={styles.label} />

							<View style={{ paddingBottom: 5 }}>
								<CustomSearchBar
									inputRef={isSplitPayment ? undefined : inputRef}
									placeholder='Type here...'
									search={reason}
									setSearch={(text) => setReason(text)}
									toggleOffset={() => { }}
									backgroundColor={theme?.iceWhite}
									leftIcon={false}
									border={true}
									height={50}
								/>
							</View>
						</View>

						{(onAccountAmount >= newTotalAmount) ? null :
							!onAccountAmount ?
								<ZenText content={"No On Account balance available. Please choose another payment method."} style={styles.errorText} /> :
								!isSplitPayment ? <ZenText content={"Your On Account balance is insufficient. Please use split payment."} style={styles.errorText} /> : null
						}

						{/* Footer Actions */}
						<View style={styles.footer}>
							<TouchableOpacity style={[styles.cancelBtn, isSplitPayment && { backgroundColor: theme?.lightBackgroundColor }]} onPress={onClose}>
								<ZenText content={isSplitPayment ? 'Back' : "Cancel"} style={styles.cancelBtnText} />
							</TouchableOpacity>
							<TouchableOpacity
								style={[styles.addBtn, disabledSave() && styles.disabledBtn]}
								disabled={disabledSave()}
								onPress={handleSaveButton}
							>
								<ZenText content={(isSplitPayment || (onAccountAmount >= newTotalAmount) || !onAccountAmount) ? 'Make Payment' : 'Split Payment'} style={[styles.addBtnText, disabledSave() && { color: theme?.disableTextColor }]} />
							</TouchableOpacity>
						</View>
					</View>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	headerCard: {
		alignItems: 'center',
		marginBottom: 20
	},
	totalAmount: { fontSize: theme?.bigFontSize + 10, fontFamily: theme?.bold, color: theme?.primary },

	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: theme?.softSlate,
	},
	headerTitle: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},
	content: {
		paddingVertical: 24
	},
	label: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
		marginBottom: 8,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	currencyPrefix: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.medium,
		color: theme?.grey,
		marginRight: 5,
	},
	footer: {
		flexDirection: 'row',
		paddingTop: 16,
		gap: 12,
		borderTopWidth: 1,
		borderTopColor: theme.softSlate,
	},
	cancelBtn: {
		flex: 1,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
		backgroundColor: theme?.iceWhite,
	},
	cancelBtnText: {
		color: theme?.disabledGray500,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
	addBtn: {
		flex: 2,
		backgroundColor: theme?.primary,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
	},
	disabledBtn: {
		backgroundColor: theme?.disableGray,
	},
	addBtnText: {
		color: theme?.light,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
	errorText: {
		color: theme?.red500,
		fontFamily: theme?.medium,
		fontSize: theme?.errorFontSize,
		textAlign: "center",
		paddingBottom: 15,
		marginTop: -10
	}
});

export default OnAccountPayment;
